/**
 * Canal écran client — synchro POS → écran face client (2e moniteur HDMI 7").
 *
 * Transport : BroadcastChannel (même origine, même machine, zéro réseau, zéro backend,
 * fonctionne hors-ligne). La fenêtre POS diffuse, la page /pos/affichage-client s'abonne.
 *
 * V1 (décision 2026-07-07) : montant + panier + poids uniquement — pas de paiement, pas de QR.
 * Voir docs/ECRAN-CLIENT-PAIEMENT.md.
 */
import { usePOSStore } from '../hooks/usePOSStore';
import { calculateSaleTotals } from '../utils/pos.utils';
import { retraitEnLigne } from '../utils/complementCommande';
import type { CartLine } from '../types/pos.types';
import { logger } from '../utils/logger';

export const CUSTOMER_DISPLAY_CHANNEL = 'folies-customer-display';

export interface CustomerDisplayLine {
  name: string;
  qty: number;
  unit_price_cents: number;
  /** Total de la ligne signé comme dans calculateSaleTotals (négatif si remboursement/retour) */
  line_total_cents: number;
  is_weighted: boolean;
  is_deposit: boolean;
  is_return: boolean;
  is_refund: boolean;
  /**
   * Date de durabilité minimale à montrer au client (œufs vendus en vrac).
   * Même source que le ticket : `print_dlc_on_ticket` sur la fiche produit et
   * `display_expiry_date` calculé par le catalogue (la DLC du lot FEFO, celui
   * qu'on vend réellement). Absente pour tout le reste.
   */
  ddm?: string | null;
}

export interface CustomerDisplayScale {
  connected: boolean;
  status: 'disconnected' | 'connected' | 'reading' | 'stable' | string;
  /** Poids NET en grammes (tare déjà déduite) — même valeur que currentScaleWeight */
  weight_grams: number;
}

export interface CustomerDisplayState {
  lines: CustomerDisplayLine[];
  total_ttc_cents: number;
  vendor_name: string | null;
  scale: CustomerDisplayScale;
  /** false si le vendeur de CE poste a perdu sa session (« Ailleurs ») ou est déconnecté
   *  → l'écran client repasse à l'accueil même si le panier local n'est pas vide */
  session_active: boolean;
  /** true pendant un encaissement (dialog paiement ouvert) → l'écran client ne part
   *  jamais en veille d'inactivité : le client doit voir son total au moment de payer */
  payment_in_progress: boolean;
  /** Paiement FOLIES en attente de confirmation : ce que le client voit avant
   *  qu'on valide sur son solde. Absent le reste du temps. */
  wallet_payment: { amount_cents: number; balance_after_cents: number } | null;
  /**
   * Retrait d'une commande en ligne déjà payée : le total du panier n'est PAS
   * ce que le client paie. Il a déjà réglé `already_paid_cents` en ligne et ne
   * doit aujourd'hui que `due_today_cents` (articles ajoutés au comptoir).
   * Absent pour toute autre vente.
   */
  online_order: { already_paid_cents: number; due_today_cents: number } | null;
  sent_at: number;
}

export type CustomerDisplayMessage =
  | { type: 'state'; payload: CustomerDisplayState }
  | { type: 'scale'; payload: CustomerDisplayScale }
  | { type: 'sale_complete'; payload: { total_ttc_cents: number } }
  // Envoyé par l'écran client à son ouverture → le POS répond par un 'state' complet
  | { type: 'hello' };

function toDisplayLines(cart: CartLine[]): CustomerDisplayLine[] {
  return cart.map((line) => {
    const signedQty = line.is_refund ? -Math.abs(line.qty) : line.qty;
    return {
      name: line.product_name || line.product?.name || 'Article',
      qty: line.qty,
      unit_price_cents: line.unit_price_cents,
      line_total_cents: Math.round(line.unit_price_cents * signedQty),
      is_weighted: !!line.product?.is_weighted,
      is_deposit: !!line.is_deposit,
      is_return: !!line.is_return,
      is_refund: !!line.is_refund,
      ddm:
        line.product?.print_dlc_on_ticket && line.product?.display_expiry_date
          ? line.product.display_expiry_date
          : null,
    };
  });
}

function buildState(): CustomerDisplayState {
  const s = usePOSStore.getState();
  const cart = s.getCurrentVendorCart();
  const totals = calculateSaleTotals(cart);
  const retrait = retraitEnLigne({
    ecomOrderId: s.ecomOrderId,
    ecomOrderPaid: s.ecomOrderPaid,
    totalTtcCents: totals.total_ttc_cents,
    paidCents: s.ecomOrderPaidCents,
    dueCents: s.ecomOrderDueCents,
    baseCents: s.ecomOrderBaseCents,
  });
  return {
    lines: toDisplayLines(cart),
    total_ttc_cents: totals.total_ttc_cents,
    vendor_name: s.currentVendorName,
    scale: {
      connected: s.isScaleConnected,
      status: s.scaleStatus,
      weight_grams: s.currentScaleWeight,
    },
    session_active: sessionActive,
    payment_in_progress: s.paymentInProgress,
    wallet_payment: s.walletPreview
      ? {
          amount_cents: s.walletPreview.amountCents,
          balance_after_cents: s.walletPreview.balanceAfterCents,
        }
      : null,
    online_order: retrait
      ? { already_paid_cents: retrait.dejaPaye, due_today_cents: retrait.aPayer }
      : null,
    sent_at: Date.now(),
  };
}

let stopBroadcast: (() => void) | null = null;

// État de session du POS (vendeur présent + lock détenu par CE poste). Poussé par
// POSWorkspace depuis VendorSessionContext ; hors-ligne le lock reste acquis (pas de
// faux « Ailleurs » au marché — seul un refus explicite du serveur le fait tomber).
let sessionActive = false;
let postStateFn: (() => void) | null = null;

export function setCustomerDisplaySessionActive(active: boolean): void {
  if (sessionActive === active) return;
  sessionActive = active;
  postStateFn?.();
}

/**
 * Démarre la diffusion de l'état POS vers l'écran client. Singleton : un seul canal
 * par fenêtre POS. Retourne la fonction d'arrêt (cleanup useEffect).
 * Ne jette jamais — l'écran client ne doit pas pouvoir gêner l'encaissement.
 */
export function startCustomerDisplayBroadcast(): () => void {
  if (typeof BroadcastChannel === 'undefined') {
    logger.warn('[customerDisplay] BroadcastChannel non supporté — écran client inactif');
    return () => {};
  }
  if (stopBroadcast) return stopBroadcast;

  const channel = new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL);

  const post = (message: CustomerDisplayMessage) => {
    try {
      channel.postMessage(message);
    } catch (err) {
      logger.error('[customerDisplay] postMessage échoué:', err);
    }
  };

  const postState = () => post({ type: 'state', payload: buildState() });

  // L'écran client vient de s'ouvrir (ou refresh) → lui renvoyer l'état courant
  channel.onmessage = (event: MessageEvent<CustomerDisplayMessage>) => {
    if (event?.data?.type === 'hello') postState();
  };

  // Panier/vendeur → état complet ; poids seul → message léger (flux balance fréquent)
  const unsubscribe = usePOSStore.subscribe((state, prev) => {
    const cartChanged =
      state.carts !== prev.carts ||
      state.vendorId !== prev.vendorId ||
      state.currentVendorName !== prev.currentVendorName ||
      state.paymentInProgress !== prev.paymentInProgress ||
      state.walletPreview !== prev.walletPreview ||
      state.ecomOrderId !== prev.ecomOrderId ||
      state.ecomOrderPaid !== prev.ecomOrderPaid ||
      state.ecomOrderPaidCents !== prev.ecomOrderPaidCents ||
      state.ecomOrderBaseCents !== prev.ecomOrderBaseCents;
    const scaleChanged =
      state.currentScaleWeight !== prev.currentScaleWeight ||
      state.scaleStatus !== prev.scaleStatus ||
      state.isScaleConnected !== prev.isScaleConnected;

    if (cartChanged) {
      postState();
    } else if (scaleChanged) {
      post({
        type: 'scale',
        payload: {
          connected: state.isScaleConnected,
          status: state.scaleStatus,
          weight_grams: state.currentScaleWeight,
        },
      });
    }
  });

  postStateFn = postState;
  postState();

  stopBroadcast = () => {
    unsubscribe();
    channel.close();
    postStateFn = null;
    stopBroadcast = null;
  };
  return stopBroadcast;
}

/**
 * Signale à l'écran client qu'une vente vient d'être encaissée (écran « Merci »).
 * À appeler AVANT de vider le panier. Fire-and-forget, jamais bloquant.
 */
export function notifyCustomerDisplaySaleComplete(totalTtcCents: number): void {
  if (typeof BroadcastChannel === 'undefined') return;
  try {
    const channel = new BroadcastChannel(CUSTOMER_DISPLAY_CHANNEL);
    channel.postMessage({
      type: 'sale_complete',
      payload: { total_ttc_cents: totalTtcCents },
    } satisfies CustomerDisplayMessage);
    channel.close();
  } catch (err) {
    logger.error('[customerDisplay] notifySaleComplete échoué:', err);
  }
}
